import { useQuery } from '@tanstack/react-query';
import { api } from '@/auth/api';
import { qk } from './query-keys';
import type {
  Load,
  LoadListItem,
  LoadPod,
  LoadStatus,
  LoadStatusHistoryEntry,
  LoadStop,
  LoadTrackingLink,
} from '@breaker/shared-types';

/**
 * Arrival/override audit row riding along on the load detail read
 * (walk-test P1-6). Override rows carry reasons like
 * "advanced to arrived by dispatcher: <text>".
 */
export interface LoadStopArrival {
  id: string;
  stopId: string;
  loadId: string;
  fleetId: string;
  lat: number | null;
  lng: number | null;
  distanceM: number | null;
  result: 'pass' | 'override';
  actorUserId: string;
  reason: string | null;
  createdAt: string;
}

/** POD row with a short-lived (10 min) presigned view URL for captured file kinds. */
export type LoadPodWithUrl = LoadPod & { url: string | null };

export interface LoadListFilters {
  status?: LoadStatus;
  driverUserId?: string;
  customerId?: string;
  limit?: number;
  offset?: number;
}

export function useLoads(fleetId: string | null, filters: LoadListFilters = {}) {
  return useQuery({
    queryKey: qk.loads.list(fleetId ?? '', filters as Record<string, unknown>),
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ loads: LoadListItem[] }>(
        `/v1/fleets/${fleetId}/loads`,
        { query: filters as Record<string, string | number | undefined> },
      ),
    select: (data) => data.loads,
  });
}

export function useLoad(fleetId: string | null, loadId: string | null) {
  return useQuery({
    queryKey: qk.loads.detail(fleetId ?? '', loadId ?? ''),
    enabled: Boolean(fleetId && loadId),
    queryFn: () =>
      api<{ load: Load; stops: LoadStop[]; arrivals: LoadStopArrival[] }>(
        `/v1/fleets/${fleetId}/loads/${loadId}`,
      ),
  });
}

export function useLoadPods(fleetId: string | null, loadId: string | null) {
  return useQuery({
    queryKey: qk.loads.pods(fleetId ?? '', loadId ?? ''),
    enabled: Boolean(fleetId && loadId),
    queryFn: () =>
      api<{ pods: LoadPodWithUrl[] }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/pods`,
      ),
    select: (data) => data.pods,
  });
}

export function useLoadHistory(fleetId: string | null, loadId: string | null) {
  return useQuery({
    queryKey: ['loads', fleetId ?? '', 'history', loadId ?? ''] as const,
    enabled: Boolean(fleetId && loadId),
    queryFn: () =>
      api<{ history: LoadStatusHistoryEntry[] }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/history`,
      ),
    select: (data) => data.history,
  });
}

export function useTrackingLinks(fleetId: string | null, loadId: string | null) {
  return useQuery({
    queryKey: ['loads', fleetId ?? '', 'tracking-links', loadId ?? ''] as const,
    enabled: Boolean(fleetId && loadId),
    queryFn: () =>
      api<{ links: LoadTrackingLink[] }>(
        `/v1/fleets/${fleetId}/loads/${loadId}/tracking-links`,
      ),
    select: (data) => data.links,
  });
}
